import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellRing,
  Gift,
  Inbox,
  Link2,
  MessageSquare,
  ShoppingBag,
  ThumbsUp,
  UserPlus,
} from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { clearAuthUser, getAuthUser } from "../utils/auth";
import { apiFetch } from "../utils/apiClient";

const UNREAD_STORAGE_KEY = "synergycoin:unreadNotifications";

const broadcastUnreadCount = (count) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(UNREAD_STORAGE_KEY, String(Math.max(count, 0)));
    window.dispatchEvent(
      new CustomEvent("synergy:unread-updated", {
        detail: Math.max(count, 0),
      }),
    );
  } catch (error) {
    // ignore storage errors; indicator falls back to zero
  }
};

const formatRelativeTime = (date) => {
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const NotificationIcon = ({ type }) => {
  const base =
    "flex h-11 w-11 items-center justify-center rounded-2xl border text-white";

  if (type === "community") {
    return (
      <div
        className={`${base} border-[#2F3148] bg-[#1B1E30] text-[#A0B9FF]`}
      >
        <MessageSquare size={18} />
      </div>
    );
  }

  if (type === "bonus") {
    return (
      <div
        className={`${base} border-[#3F2F88] bg-[#201147] text-[#C9B6FF]`}
      >
        <Gift size={18} />
      </div>
    );
  }

  if (type === "referral") {
    return (
      <div
        className={`${base} border-[#2E3A78] bg-[#0F1A3A] text-[#8FB4FF]`}
      >
        <UserPlus size={18} />
      </div>
    );
  }

  if (type === "transaction") {
    return (
      <div
        className={`${base} border-[#4A5D3A] bg-[#2A3A1F] text-[#A8D5A0]`}
      >
        <ShoppingBag size={18} />
      </div>
    );
  }

  return (
    <div className={`${base} border-[#364060] bg-[#151A2C] text-[#9CAEDB]`}>
      <Inbox size={18} />
    </div>
  );
};

const resolveTypeLabel = (type) => {
  switch (type) {
    case "community":
      return "Community";
    case "bonus":
      return "Bonus";
    case "referral":
      return "Referral";
    case "transaction":
      return "Transaction";
    default:
      return "System";
  }
};

export default function Notifications() {
  const navigate = useNavigate();
  const user = useMemo(() => getAuthUser(), []);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignOut = () => {
    clearAuthUser();
    broadcastUnreadCount(0);
    navigate("/login", { replace: true });
  };

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const response = await apiFetch("/notifications", {
        headers: { "x-user-id": user.id },
      });

      const parsedNotifications = (response?.notifications || []).map(
        (item) => ({
          ...item,
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          type: item.type || "system",
        }),
      );

      setNotifications(parsedNotifications);
      const unread = response?.unread || 0;
      setUnreadCount(unread);
      broadcastUnreadCount(unread);
    } catch (requestError) {
      setError(requestError.message || "Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await apiFetch("/notifications/mark-all-read", {
        method: "POST",
        headers: { "x-user-id": user.id },
      });
      const updated = notifications.map((item) => ({ ...item, read: true }));
      setNotifications(updated);
      setUnreadCount(0);
      broadcastUnreadCount(0);
    } catch (markAllError) {
      setError(markAllError.message || "Failed to mark notifications as read.");
    }
  };

  const markNotificationRead = async (id, currentState) => {
    if (!user?.id) return;
    try {
      await apiFetch(`/notifications/${id}/read`, {
        method: "POST",
        headers: { "x-user-id": user.id },
        data: { read: !currentState },
      });

      const updated = notifications.map((item) =>
        item.id === id ? { ...item, read: true } : item,
      );
      setNotifications(updated);
      const unread = updated.filter((item) => !item.read).length;
      setUnreadCount(unread);
      broadcastUnreadCount(unread);
    } catch (toggleError) {
      setError(toggleError.message || "Failed to update notification.");
    }
  };

  const filteredNotifications = useMemo(() => {
    const data = notifications.filter((item) => {
      if (filter === "unread") return !item.read;
      if (filter === "referrals") return item.type === "referral";
      if (filter === "bonus") return item.type === "bonus";
      if (filter === "community") return item.type === "community";
      if (filter === "transaction") return item.type === "transaction";
      return true;
    });

    return data.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [notifications, filter]);

  return (
    <DashboardLayout
      user={user}
      onSignOut={handleSignOut}
      activeKey="notifications"
    >
      {() => (
        <div className="space-y-8">
          <header className="space-y-3">
            <div className="text-sm text-white/50">
              Dashboard <span className="mx-2 text-white/30">›</span>{" "}
              Notification
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Notifications
            </h1>
            <p className="text-sm text-white/60">
              Every referral milestone, bonus credit, follow event, and important
              alert lands here first.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-xs text-white/50">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                <BellRing size={14} />
                {unreadCount} unread alerts
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                <Link2 size={14} /> Referral bonus tracked automatically
              </span>
            </div>
          </header>

          <section className="rounded-3xl border border-white/10 bg-black/60 p-5 shadow-[0_18px_60px_rgba(16,9,38,0.4)] backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-full border border-white/10 bg-black/40 p-1 text-xs text-white/70">
                {[
                  { key: "all", label: "All" },
                { key: "unread", label: "Unread" },
                { key: "referrals", label: "Referrals" },
                { key: "bonus", label: "Bonuses" },
                { key: "community", label: "Community" },
                { key: "transaction", label: "Transactions" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className={`rounded-full px-4 py-2 transition ${
                      filter === key
                        ? "bg-[#8C3CDD] text-white"
                        : "hover:bg-white/5"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs text-white/70">
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-2 transition hover:bg-white/10"
                >
                  Mark all as read
                </button>
                <button
                  type="button"
                  onClick={fetchNotifications}
                  className="rounded-full border border-[#8C3CDD]/30 bg-[#8C3CDD]/15 px-4 py-2 text-[#C1A5FF] transition hover:bg-[#8C3CDD]/25"
                >
                  Refresh
                </button>
              </div>
            </div>
          </section>

          {error && (
            <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <section className="rounded-3xl border border-white/10 bg-black/60 p-6 shadow-[0_24px_80px_rgba(16,8,38,0.45)] backdrop-blur">
            <div className="mb-4 flex items-center justify-between gap-3 text-sm text-white/60">
              <span className="inline-flex items-center gap-2">
                <Inbox size={16} />
                {loading
                  ? "Loading notifications…"
                  : `Showing ${filteredNotifications.length} of ${notifications.length} notifications`}
              </span>
            </div>

            {loading ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/10 bg-black/50 px-6 py-12 text-center text-white/50">
                <Inbox size={28} className="animate-pulse" />
                <p className="text-sm">Fetching the latest updates…</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/10 bg-black/50 px-6 py-12 text-center text-white/50">
                <Inbox size={28} />
                <p className="text-sm">No notifications match this filter.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredNotifications.map((notification) => (
                  <article
                    key={notification.id}
                    className={`flex gap-4 rounded-2xl border border-white/10 bg-black/40 p-5 transition hover:border-[#8C3CDD]/40 ${
                      notification.read ? "opacity-75" : ""
                    }`}
                  >
                    <NotificationIcon type={notification.type} />
                    <div className="flex-1 space-y-3">
                      <header className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 text-xs text-white/40">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                              {notification.type === "bonus" ? (
                                <>
                                  <Gift size={12} />
                                  {resolveTypeLabel(notification.type)}
                                </>
                              ) : notification.type === "community" ? (
                                <>
                                  <ThumbsUp size={12} />
                                  {resolveTypeLabel(notification.type)}
                                </>
                              ) : notification.type === "referral" ? (
                                <>
                                  <UserPlus size={12} />
                                  {resolveTypeLabel(notification.type)}
                                </>
                              ) : notification.type === "transaction" ? (
                                <>
                                  <ShoppingBag size={12} />
                                  {resolveTypeLabel(notification.type)}
                                </>
                              ) : (
                                <>
                                  <Inbox size={12} />
                                  {resolveTypeLabel(notification.type)}
                                </>
                              )}
                            </span>
                            <span>{formatRelativeTime(notification.createdAt)}</span>
                          </div>
                          <h2 className="mt-1 text-base font-semibold text-white">
                            {notification.title}
                          </h2>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            markNotificationRead(notification.id, notification.read)
                          }
                          className={`rounded-full border px-4 py-2 text-xs transition ${
                            notification.read
                              ? "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
                              : "border-[#8C3CDD]/40 bg-[#8C3CDD]/20 text-white hover:bg-[#8C3CDD]/30"
                          }`}
                        >
                          {notification.read ? "Read" : "Mark as read"}
                        </button>
                      </header>

                      <p className="text-sm text-white/70">
                        {notification.message}
                      </p>

                      {notification.payload?.packageName && notification.type === "transaction" && (
                        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-xs text-white/50">
                          <span>
                            Package:{" "}
                            <span className="font-semibold text-white">
                              {notification.payload.packageName}
                            </span>
                          </span>
                          {notification.payload?.amount && (
                            <span>
                              Amount:{" "}
                              <span className="font-semibold text-[#8C3CDD]">
                                ${Number(notification.payload.amount).toFixed(2)}
                              </span>
                            </span>
                          )}
                          {notification.payload?.discount && notification.payload.discount > 0 && (
                            <span>
                              Discount:{" "}
                              <span className="font-semibold text-green-400">
                                ${Number(notification.payload.discount).toFixed(2)}
                              </span>
                            </span>
                          )}
                        </div>
                      )}

                      {notification.payload?.referralEmail && (
                        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-xs text-white/50">
                          <span>
                            Referred:{" "}
                            <span className="font-semibold text-white">
                              {notification.payload.referralEmail}
                            </span>
                          </span>
                          {notification.payload?.bonus && (
                            <span>
                              Bonus:{" "}
                              <span className="font-semibold text-[#8C3CDD]">
                                {notification.payload.bonus}
                              </span>
                            </span>
                          )}
                        </div>
                      )}

                      <footer className="flex flex-wrap items-center justify-between gap-3 text-xs text-white/40">
                        <span>
                          Received on{" "}
                          {notification.createdAt.toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                        {notification.payload?.postId && (
                          <button
                            type="button"
                            onClick={() => navigate("/community")}
                            className="rounded-full border border-[#8C3CDD]/30 px-3 py-1 text-[#C7B5FF] transition hover:bg-[#8C3CDD]/15"
                          >
                            View post
                          </button>
                        )}
                      </footer>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </DashboardLayout>
  );
}
